// components/playground/sections/ChangesBreakdownSection.jsx
import React, { useMemo } from 'react';
import { 
  getMonthName, 
  formatNumber, 
} from '../../../../utils/plScenarioHelpers';

const ChangesBreakdownSection = ({
  currentScenario,
  adjustmentCount,
  totalAdjustmentCount,
  historicalAdjustmentCount,
  customItemCount,
  currentYear,
  currentMonth,
  lineItems,
}) => {
  // ✅ Override with actual current month if passed value is wrong
  const today = new Date();
  const actualCurrentMonth = today.getMonth() + 1; // 1-12
  const actualCurrentYear = today.getFullYear();
  
  // Use actual values to be safe
  const safeCurrentMonth = actualCurrentMonth;
  const safeCurrentYear = actualCurrentYear;
  
  console.log('📅 Date Info:', { 
    today: today.toLocaleDateString(),
    actualCurrentYear,
    actualCurrentMonth,
    passedCurrentYear: currentYear,
    passedCurrentMonth: currentMonth,
  });

  // ✅ FIXED: Proper year-aware active check
  const isMonthActive = (year, month) => {
    if (year < safeCurrentYear) return false;  // Past year
    if (year > safeCurrentYear) return true;   // Future year
    return month >= safeCurrentMonth;          // Same year: check month
  };

  // ✅ Process adjustments - group by account + year
  const groupedAdjustments = useMemo(() => {
    if (!currentScenario?.adjustments) return [];

    const groups = {};

    Object.entries(currentScenario.adjustments).forEach(([key, value]) => {
      const [year, accountId, month] = key.split('-');
      const yearNum = parseInt(year);
      const monthNum = parseInt(month);
      
      // ✅ Match by account_id
      const account = lineItems.find(item => 
        item.account_id === accountId || 
        item.id === accountId
      );
      
      // ✅ Use account_name field
      const accountName = account?.account_name || 
                        account?.name || 
                        accountId;
      
      // ✅ Use account_type as the parent category
      const parentCategory = account?.account_type || 
                          account?.classification ||
                          'Uncategorized';
      
      const groupKey = `${accountId}-${yearNum}`;
      
      if (!groups[groupKey]) {
        groups[groupKey] = {
          accountId,
          accountName,
          parentCategory,
          year: yearNum,
          values: {},
        };
      }
      
      groups[groupKey].values[monthNum] = value;
    });

    return Object.values(groups).sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.accountName.localeCompare(b.accountName);
    });
  }, [currentScenario, lineItems, safeCurrentYear, safeCurrentMonth]);

  // ✅ Also update customItemRows to use the same pattern
  const customItemRows = useMemo(() => {
    if (!currentScenario?.custom_items) return [];

    const rows = [];

    Object.entries(currentScenario.custom_items).forEach(([groupKey, items]) => {
      items.forEach(item => {
        const valuesByYear = {};
        
        Object.entries(item.monthly_values || item.values || {}).forEach(([month, value]) => {
          if (value && value !== 0) {
            const monthNum = parseInt(month);
            const year = item.year || safeCurrentYear;
            
            if (!valuesByYear[year]) {
              valuesByYear[year] = {};
            }
            valuesByYear[year][monthNum] = value;
          }
        });

        Object.entries(valuesByYear).forEach(([year, values]) => {
          const yearNum = parseInt(year);
          
          // ✅ Extract parent from groupKey (e.g., "2026-Revenue-custom")
          const parts = groupKey.split('-');
          const parentCategory = parts.length > 1 ? parts[1] : 'Custom Items';
          
          rows.push({
            accountName: item.name,
            parentCategory,
            year: yearNum,
            values,
            isCustom: true,
          });
        });
      });
    });

    return rows.sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.accountName.localeCompare(b.accountName);
    });
  }, [currentScenario, safeCurrentYear, safeCurrentMonth]);

  if (!currentScenario) {
    return null;
  }

  if (groupedAdjustments.length === 0 && customItemRows.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="font-semibold text-gray-900 mb-4">Changes in This Scenario</h4>
        <div className="text-sm text-gray-500 text-center py-8 bg-gray-50 rounded-lg">
          No changes in this scenario
        </div>
      </div>
    );
  }

  const renderChangesTable = (changes, title, icon) => {
    if (changes.length === 0) return null;

    return (
      <div className="mb-6 last:mb-0">
        <div className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <span>{icon}</span>
          <span>{title}</span>
        </div>
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {/* ✅ Horizontal scroll wrapper */}
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  {/* ✅ Account Column */}
                  <th className="px-3 py-2 text-left font-medium text-gray-700 border-r border-gray-200 min-w-[180px]">
                    Account
                  </th>
                  {/* ✅ Category Column - MAKE SURE THIS IS HERE */}
                  <th className="px-3 py-2 text-left font-medium text-gray-700 border-r border-gray-200 min-w-[140px]">
                    Category
                  </th>
                  {/* ✅ Year Column */}
                  <th className="px-3 py-2 text-left font-medium text-gray-700 border-r border-gray-200 w-16">
                    Year
                  </th>
                  {/* ✅ Monthly Values Column */}
                  <th className="px-3 py-2 text-left font-medium text-gray-700">
                    Monthly Values
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {changes.map((change, idx) => {
                  const sortedMonths = Object.keys(change.values)
                    .map(m => parseInt(m))
                    .sort((a, b) => a - b);

                  return (
                    <tr 
                      key={`${change.accountId || 'custom'}-${change.year}-${idx}`}
                      className="hover:bg-gray-50"
                    >
                      {/* Account Name Cell */}
                      <td className="px-3 py-2 text-gray-900 border-r border-gray-200">
                        <div className="font-medium">{change.accountName}</div>
                        {change.isCustom && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">
                            Custom
                          </span>
                        )}
                      </td>
                      
                      {/* ✅ Category Cell - MAKE SURE THIS IS HERE */}
                      <td className="px-3 py-2 text-gray-600 border-r border-gray-200 text-xs">
                        {change.parentCategory}
                      </td>
                      
                      {/* Year Cell */}
                      <td className="px-3 py-2 text-gray-600 border-r border-gray-200 font-medium">
                        {change.year}
                      </td>
                      
                      {/* Monthly Values Cell */}
                      <td className="px-3 py-2">
                        <div className="overflow-x-auto">
                          <table className="min-w-max">
                            <tbody>
                              {/* Month headers */}
                              <tr>
                                {sortedMonths.map(month => {
                                  const active = isMonthActive(change.year, month);
                                  return (
                                    <td 
                                      key={`month-${month}`} 
                                      className={`px-2 py-1 text-xs font-medium border-r border-gray-200 last:border-r-0 whitespace-nowrap ${
                                        active ? 'text-gray-900' : 'text-gray-400'
                                      }`}
                                    >
                                      {getMonthName(month)}
                                    </td>
                                  );
                                })}
                              </tr>
                              {/* Values */}
                              <tr>
                                {sortedMonths.map(month => {
                                  const active = isMonthActive(change.year, month);
                                  return (
                                    <td 
                                      key={`value-${month}`} 
                                      className={`px-2 py-1 text-xs font-mono border-r border-gray-200 last:border-r-0 whitespace-nowrap ${
                                        active ? 'text-gray-900 font-bold' : 'text-gray-400'
                                      }`}
                                    >
                                      ${formatNumber(change.values[month])}
                                    </td>
                                  );
                                })}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <h4 className="font-semibold text-gray-900 mb-4">Changes in This Scenario</h4>
      
      <div className="text-sm text-gray-600 mb-4">
        {adjustmentCount} active adjustment{adjustmentCount !== 1 ? 's' : ''}
        {historicalAdjustmentCount > 0 && ` • ${historicalAdjustmentCount} historical`}
        {customItemCount > 0 && ` • ${customItemCount} custom item${customItemCount !== 1 ? 's' : ''}`}
      </div>

      {/* ✅ Separate table for adjustments */}
      {renderChangesTable(
        groupedAdjustments, 
        `📊 Manual Adjustments (${groupedAdjustments.length})`,
        'No manual adjustments'
      )}

      {/* ✅ Separate table for custom items */}
      {renderChangesTable(
        customItemRows, 
        `➕ Custom Line Items (${customItemRows.length})`,
        'No custom items'
      )}
    </div>
  );
};

export default ChangesBreakdownSection;